import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormInput from '@/components/hook-form-components/FormInput';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { useGetCities } from '@/features/cities/api/use-get-cities';
import { useGetCounties } from '@/features/counties/api/use-get-counties';
import { useGetVillages } from '@/features/villages/api/use-get-villages';
import { useScript } from '@/hooks/use-script';

import './styles/in-ads-widget.css';
import { initializeInAdsMap } from './utils/init-in-ads-maps';

type InAdsWidgetProps = {
  hideMap?: boolean;
};

const IN_ADS_MAP_SCRIPT_SRC = 'https://inaadress.maaamet.ee/inaadress/js/inaadress.min.js?d=20220510';
const IN_ADS_MAP_SCRIPT_ID = 'inaadress-script';
const IN_ADS_MAP_ELEMENT_ID = 'InAadressDiv';

const InAdsWidget = ({ hideMap }: InAdsWidgetProps) => {
  const { setValue, control } = useFormContext();
  const [isScriptLoaded] = useScript(IN_ADS_MAP_SCRIPT_SRC, IN_ADS_MAP_SCRIPT_ID);

  const { t, i18n } = useTranslation('inAdsWidget');

  const countiesQuery = useGetCounties();
  const citiesQuery = useGetCities();
  const villagesQuery = useGetVillages();

  useEffect(() => {
    if (isScriptLoaded && countiesQuery.data && citiesQuery.data && villagesQuery.data) {
      initializeInAdsMap({
        hideMap,
        i18n,
        counties: countiesQuery.data,
        cities: citiesQuery.data,
        villages: villagesQuery.data,
        mapElementId: IN_ADS_MAP_ELEMENT_ID,
        onAddressSelected: (details) => {
          setValue('countyId', details.countyId.toString());
          setValue('cityId', details.cityId.toString());
          setValue('villageId', details.villageId.toString());
          setValue('street', details.street);
          setValue('houseNumber', details.houseNumber);
          setValue('flatNumber', details.flatNumber);
          setValue('fullAddress', details.fullAddress);
        },
      });
    }
  }, [isScriptLoaded, hideMap, i18n, setValue, countiesQuery.data, citiesQuery.data, villagesQuery.data]);

  return (
    <div className="flex flex-col gap-4 w-full h-full">
      <div className="mb-3 flex-1" id={IN_ADS_MAP_ELEMENT_ID}></div>
      <div className="flex sm:flex-row flex-col gap-4 sm:gap-5">
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('county')}</label>
            <Controller
              control={control}
              name="countyId"
              render={({ field }) => (
                <Select
                  value={field.value?.toString()}
                  onItemSelect={field.onChange}
                  className="flex-1"
                  placeholder={t('selectCounty')}
                  options={
                    countiesQuery?.data?.map((el) => ({
                      value: el.id.toString(),
                      label: el.county,
                    })) ?? []
                  }
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('city')}</label>
            <Controller
              name="cityId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.toString()}
                  onItemSelect={field.onChange}
                  className="flex-1"
                  options={
                    citiesQuery?.data?.map((el) => ({
                      value: el.id.toString(),
                      label: el.city,
                    })) ?? []
                  }
                  placeholder={t('selectCity')}
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('houseNumber')}</label>
            <Controller control={control} name="houseNumber" render={({ field }) => <Input {...field} placeholder={t('houseNumber')} />} />
          </div>
        </div>
        <div className="flex flex-col gap-4 flex-1">
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('village')}</label>
            <Controller
              name="villageId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.toString()}
                  onItemSelect={field.onChange}
                  className="flex-1"
                  placeholder={t('selectVillage')}
                  options={
                    villagesQuery?.data?.map((el) => ({
                      value: el.id.toString(),
                      label: el.village,
                    })) ?? []
                  }
                />
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('street')}</label>
            <FormInput name="street" control={control} placeholder={t('street')} />
          </div>
          <div className="flex flex-col gap-2">
            <label className="block font-bold text-gray-700 text-sm">{t('flatNumber')}</label>
            <FormInput name="flatNumber" control={control} placeholder={t('flatNumber')} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label className="block font-bold text-gray-700 text-sm">{t('addressSpecification')}</label>
        <Controller
          control={control}
          name="fullAddress"
          render={({ field }) => <span className="px-4 py-3 text-gray-10 text-ellipsis overflow-hidden">{field.value}</span>}
        />
      </div>
    </div>
  );
};

export default InAdsWidget;
